'use client';

import Card from '@/_components/Card';
import SkeletonCard from '@/_components/Skeleton/Card';
import { useCustomLocale } from '@/_hooks/useCustomLocale';
import productService from '@/_service/product.service';
import { IRedux } from '@/_store/types';
import type { ICategory } from '@/_types';
import type { IProduct } from '@/entities/product/model/types';
import { Box, Text } from '@chakra-ui/react';
import { useInfiniteQuery } from '@tanstack/react-query';
import useEmblaCarousel from 'embla-carousel-react';
import WheelGesturesPlugin from 'embla-carousel-wheel-gestures';
import { Suspense, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import LoadingSpinner from './components/loading-spinner';
import { getProductQueryParams, useMenuContext } from './utils';
import { useTranslations } from 'next-intl';

const OPTIONS = { dragFree: true };

const Section = ({
  category,
  initialProducts,
}: {
  category: ICategory;
  initialProducts?: { products: IProduct[]; count: number };
}) => {
  const t = useTranslations();
  const locale = useCustomLocale();
  const settings = useSelector((state: IRedux) => state.settings);
  const { platform_id, effectiveMenuId, deliveryType } = useMenuContext();

  // Server prefetch runs without menu_id; only seed initialData before branch menu is known.
  const shouldUseInitialData = Boolean(initialProducts) && !effectiveMenuId;

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: [
        'section-products',
        settings?.data?.shipper_id,
        effectiveMenuId,
        deliveryType,
        category.id,
      ],
      queryFn: async ({
        pageParam = 1,
      }): Promise<{
        products: IProduct[];
        count: number;
        pageParam: number;
      }> => {
        const limit = 20;
        const response = await productService.getAll(
          getProductQueryParams(pageParam, {
            limit: limit,
            effectiveMenuId,
            deliveryType,
            category_id: category.id,
          }),
          settings?.data?.shipper_id,
          platform_id,
          '',
        );

        return {
          products: response?.data?.products || [],
          count: response?.data?.count || 0,
          pageParam,
        };
      },
      initialPageParam: 1,
      getNextPageParam: (lastPage, allPages): number | undefined => {
        const loadedProductCount = allPages.reduce(
          (total, page) => total + page.products.length,
          0,
        );
        const totalPages = Math.ceil(lastPage.count / 12);

        return loadedProductCount < lastPage.count &&
          allPages.length < totalPages
          ? allPages.length + 1
          : undefined;
      },
      select: (data): { products: IProduct[]; count: number } => ({
        products: data.pages.flatMap((page) => page.products),
        count: data.pages[0]?.count || 0,
      }),
      initialData: shouldUseInitialData
        ? {
            pages: [
              {
                products: initialProducts!.products,
                count: initialProducts!.count,
                pageParam: 1,
              },
            ],
            pageParams: [1],
          }
        : undefined,
      refetchOnWindowFocus: false,
    });

  const [emblaRef] = useEmblaCarousel(OPTIONS, [WheelGesturesPlugin()]);
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const skeletonRef = useRef<HTMLDivElement | null>(null);
  const loadingRef = useRef(false);

  useEffect(() => {
    loadingRef.current = isFetchingNextPage;
  }, [isFetchingNextPage]);

  useEffect(() => {
    const el = skeletonRef.current;
    const root = viewportRef.current;
    if (!el || !hasNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && hasNextPage && !loadingRef.current) {
            loadingRef.current = true;
            fetchNextPage();
          }
        });
      },
      { root, threshold: 0.5 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage]);

  if (!data?.products?.length) return null;

  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Box as='section' pb={{ base: 1, lg: 3 }} pt={{ base: 5, lg: 8 }}>
        <Text
          px={{ base: 4, lg: 14 }}
          lineHeight={1}
          fontSize={{ base: 'xl', lg: '2xl' }}
          fontWeight={700}
        >
          {category?.title?.[locale]}
        </Text>
        <Box className='embla embla--products'>
          <Box
            px={{ base: 4, lg: 14 }}
            py={{ base: 4, lg: 5 }}
            className='embla__viewport'
            ref={(el: HTMLDivElement | null) => {
              emblaRef(el);
              viewportRef.current = el as HTMLDivElement | null;
            }}
          >
            <Box className='embla__container'>
              {data?.products?.map((product) => (
                <div
                  className='embla__slide'
                  key={`${product.id}-${product?.image}`}
                >
                  <Card product={product} category={category} />
                </div>
              ))}
              {hasNextPage && (
                <div className='embla__slide' ref={skeletonRef}>
                  <SkeletonCard />
                </div>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Suspense>
  );
};

export default Section;
